// Stripe Checkout client. The browser never sends a price: only the product,
// the e-mail, an optional discount code and the two legally required consents.
// The server prices the order and answers with a hosted-checkout URL.
import { api } from "@/lib/api";

export const PRODUCTS = {
  report: { key: "report", name: { de: "Ausführlicher Bericht", en: "Detailed report" } },
  consulting: { key: "consulting", name: { de: "Beratung 30 Tage", en: "Consulting, 30 days" } },
};

export async function postCheckout(token, { product, email, discountCode }) {
  const body = { product, email: String(email).trim(), accept_terms: true, accept_waiver: true };
  const code = (discountCode || "").trim();
  if (code) body.discount_code = code;
  const { data } = await api.post(`/report/${encodeURIComponent(token)}/checkout`, body);
  return data;
}

export async function validateDiscountCode(code, product) {
  const { data } = await api.post("/discount/validate", { code: String(code).trim(), product });
  return data;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const isValidEmail = (v) => typeof v === "string" && v.trim().length <= 200 && EMAIL_RE.test(v.trim());

// The pay button is enabled only with a valid e-mail, both consents and no
// request in flight (which also blocks a double submit).
export const canCheckout = ({ email, acceptTerms, acceptWaiver, busy } = {}) =>
  !busy && acceptTerms === true && acceptWaiver === true && isValidEmail(email);

export const formatPrice = (n, lang = "de") => {
  const v = Number(n);
  const txt = Number.isInteger(v) ? String(v) : v.toFixed(2);
  return lang === "en" ? `€${txt}` : `${txt.replace(".", ",")} €`;
};

// Turns a /discount/validate answer into what the dialog shows. Anything that
// is not a clean `valid: true` with a numeric price is just "invalid".
export function discountView(base, res) {
  if (!res || res.valid !== true || typeof res.final_price_eur !== "number" || !(res.final_price_eur >= 0)) {
    return { valid: false, final: base, saved: 0 };
  }
  const final = res.final_price_eur;
  return { valid: true, final, saved: Math.max(0, Math.round((base - final) * 100) / 100) };
}

const ERR = {
  de: {
    unavailable: "Zahlung derzeit nicht verfügbar",
    invalid: "Bitte prüfen Sie Ihre Angaben.",
    invalidCode: "Ungültiger Rabattcode.",
    conflictReport: "Für diesen Bericht liegt bereits eine Bestellung vor.",
    conflictConsulting: "Die Beratung ist für diesen Bericht bereits gebucht oder noch nicht möglich.",
    rate: "Zu viele Versuche. Bitte versuchen Sie es später erneut.",
    upstream: "Der Zahlungsdienst ist gerade nicht erreichbar. Bitte versuchen Sie es in Kürze erneut.",
    generic: "Die Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.",
  },
  en: {
    unavailable: "Payment is currently unavailable",
    invalid: "Please check your details.",
    invalidCode: "Invalid discount code.",
    conflictReport: "There is already an order for this report.",
    conflictConsulting: "Consulting is already booked for this report or not possible yet.",
    rate: "Too many attempts. Please try again later.",
    upstream: "The payment provider is unreachable right now. Please try again shortly.",
    generic: "Payment could not be started. Please try again.",
  },
};

export function checkoutErrorMessage(err, { product = "report", lang = "de" } = {}) {
  const M = ERR[lang] || ERR.de;
  const status = err && err.response && err.response.status;
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (status === 503) return M.unavailable;
  if (status === 409) return product === "consulting" ? M.conflictConsulting : M.conflictReport;
  if (status === 429) return M.rate;
  if (status === 502) return M.upstream;
  if (status === 422) return typeof detail === "string" && /rabatt/i.test(detail) ? M.invalidCode : M.invalid;
  return M.generic;
}

// Only Stripe Checkout and our own site may be followed (https only).
const SITE_ORIGINS = ["https://positivup.com", "https://www.positivup.com"];
export function safeRedirectUrl(url) {
  if (typeof url !== "string") return null;
  try {
    const u = new URL(url);
    if (u.protocol !== "https:" && !(typeof window !== "undefined" && u.origin === window.location.origin)) return null;
    const own = typeof window !== "undefined" ? window.location.origin : "";
    if (u.origin === "https://checkout.stripe.com" || SITE_ORIGINS.includes(u.origin) || (own && u.origin === own)) {
      return u.toString();
    }
    return null;
  } catch {
    return null;
  }
}
